import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, Trash2, Package, Lightbulb, Pencil, FileText, Loader2, Target, MapPin, Tag, Eye } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { type Campaign } from './types';
import { formatDate, formatBudget } from './utils';

interface ListViewCampaignCardProps {
    campaign: Campaign;
    showDeleteButton?: boolean;
    showEditButton?: boolean;
    onEdit?: (campaignId: string) => void;
    onView?: (campaignId: string) => void;
    onDelete?: (campaignId: number) => void;
    onGenerateBrief?: (campaign: Campaign) => void;
    isGeneratingBrief?: boolean;
    generatingBriefCampaignId?: number;
    canEdit?: boolean;
    canDelete?: boolean;
}

export function ListViewCampaignCard({
    campaign,
    showDeleteButton = false,
    showEditButton = false,
    onEdit,
    onDelete,
    onView,
    onGenerateBrief,
    isGeneratingBrief = false,
    generatingBriefCampaignId,
    canEdit = false,
    canDelete = false
}: ListViewCampaignCardProps) {
    return (
        <div className="relative bg-[#f8f5f0] rounded-2xl overflow-hidden">
            <div aria-hidden className="pointer-events-none absolute inset-0 border border-[#e0d6c9] rounded-2xl" />
            <div className="relative flex flex-col">
                <div className="h-48 w-full bg-gray-100 border-t border-l border-r border-[#e0d6c9] rounded-t-2xl">
                    {campaign.image ? (
                        <ImageWithFallback
                            src={campaign.image}
                            alt={campaign.name}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
                            No image available
                        </div>
                    )}
                </div>

                <div className="space-y-4 p-5">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <h4 className="truncate text-base font-semibold text-[#3e2723]">{campaign.name}</h4>
                        </div>
                        <Badge
                            variant="secondary"
                            className={`flex-shrink-0 rounded-md px-2 py-1 text-xs ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                                campaign.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}
                        >
                            {campaign.status === 'active' ? 'Active' :
                                campaign.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                        </Badge>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <span className="truncate max-w-[180px]">{campaign.businessUnit || 'Unknown location'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-gray-500" />
                            <span className="truncate max-w-[180px]">{campaign.category}</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                        <div className="flex items-center gap-2 font-semibold text-[#101828]">
                            <Target className="h-4 w-4" />
                            <span>
                                {campaign.campaignType ? campaign.campaignType :
                                    campaign.status === 'active' ? 'Lead Capturing' : 'Awareness'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                            <Package className="h-4 w-4" />
                            <span>
                                {(campaign.products?.length ?? 0)}{' '}
                                {(campaign.products?.length ?? 0) === 1 ? 'Product' : 'Products'}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}</span>
                        </div>
                    </div>

                    {campaign.concept && (
                        <div className="flex w-fit items-center gap-2 rounded-md bg-purple-50 px-3 py-2 text-sm text-purple-700">
                            <Lightbulb className="h-4 w-4" />
                            <span className="max-w-[240px] truncate font-medium">{campaign.concept}</span>
                        </div>
                    )}
                    {campaign.targetProfile && (
                        <div className="flex w-fit items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
                            <Target className="h-4 w-4" />
                            <span className="max-w-[240px] truncate font-medium">{campaign.targetProfile}</span>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2">
                        {onView && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                onClick={() => onView(campaign.id.toString())}
                                title="Open showcase page"
                            >
                                <Eye className="h-4 w-4" />
                            </Button>
                        )}
                        {onGenerateBrief && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-amber-600 hover:bg-orange-50 hover:text-amber-700"
                                onClick={() => onGenerateBrief(campaign)}
                                title="Generate campaign brief"
                            >
                                {isGeneratingBrief && generatingBriefCampaignId === campaign.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <FileText className="h-4 w-4" />
                                )}
                            </Button>
                        )}

                        {(showEditButton || canEdit) && onEdit && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                                onClick={() => onEdit(campaign.id.toString())}
                                title="Edit campaign"
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>
                        )}

                        {(showDeleteButton || canDelete) && onDelete && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                                onClick={() => onDelete(campaign.id)}
                                title="Delete campaign"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
